<template>
    <div :class="['bg-white w-full max-w-[284px] relative pb-4 border-l border-won-[#E9EEF2]', showResumeScorer ? 'block' : 'hidden']">
        <div class="px-4 pt-4">
            <div class="flex justify-between items-center mb-2">
                <h3 class="text-[20px] font-semibold text-won-blue">
                    Improve your Resume
                </h3>

                <span @click="scorerStore.hideResumeScorer" class="cursor-pointer">
                    <XIcon />
                </span>
            </div>

            <ScoreCircle :progress="score?.totalScore || 0" />

            <button
                class="text-won-green w-full p-[9px] flex justify-center items-center bg-won-green-light mt-4 rounded"
                @click="scorerStore.setShowBreakdown"
            >
                View Score Breakdown
            </button>
        </div>

        <hr class="my-[22px] mx-4" />

        <div class="flex items-center gap-3 px-4">
            <div
                class="w-full"
                v-tooltip="{
                    content: TOOLTIPS_CONTENT.bullets,
                    placement: 'bottom',
                    popperClass
                }"
            >
                <div class="h-[6px] bg-[#f1f1f1] rounded-full overflow-hidden">
                    <div class="bg-won-blue-light h-[6px] bullets-progress"></div>
                </div>
                <span class="text-[12px] mt-[3px] block w-full text-center">Bullets</span>
            </div>

            <div
                class="w-full"
                v-tooltip="{
                    content: TOOLTIPS_CONTENT.format,
                    placement: 'bottom',
                    popperClass
                }"
            >
                <div class="h-[6px] bg-[#f1f1f1] rounded-full overflow-hidden">
                    <div class="bg-won-purple w-[50%] h-[6px] format-progress"></div>
                </div>
                <span class="text-[12px] mt-[3px] block w-full text-center">Format</span>
            </div>

            <div
                class="w-full"
                v-tooltip="{
                    content: TOOLTIPS_CONTENT.relevantInfo,
                    placement: 'bottom',
                    popperClass
                }"
            >
                <div class="h-[6px] bg-[#f1f1f1] rounded-full overflow-hidden">
                    <div class="bg-won-orange w-[50%] h-[6px] info-progress"></div>
                </div>
                <span class="text-[12px] mt-[3px] block w-full text-center">Info</span>
            </div>

            <div
                class="w-full"
                v-tooltip="{
                    
                    content: TOOLTIPS_CONTENT.skills,
                    placement: 'bottom',
                    popperClass
                }"
            >
                <div class="h-[6px] bg-[#f1f1f1] rounded-full overflow-hidden">
                    <div class="bg-won-yellow w-[50%] h-[6px] skills-progress"></div>
                </div>
                <span class="text-[12px] mt-[3px] block w-full text-center">Skills</span>
            </div>
        </div>

        <div class="mt-1 pt-4 px-4 flex flex-col gap-5 max-h-[calc(100vh-333px)] pb-[100px] overflow-y-auto">
            <BpWarning
                v-for="(warning, index) in warnings"
                :warning="warning"
                :key="`${warning.warningType}-${index}`"
                @warning:hover="warningHoverHandler"
                @warning:leave="warningHoverResetHandler"
            />
        </div>

        <div
            class="absolute bottom-4 left-0 right-0 cursor-pointer text-[14px] leading-[17px] font-medium text-won-green block text-center font-Inter"
            @click="scorerStore.hideResumeScorer"
        >
            Hide Details
        </div>
    </div>
</template>

<script setup>
import {computed, ref, onBeforeUnmount, watch} from "vue";
import { storeToRefs } from "pinia";
import { useResumeScorerStore } from "../../store/scorer.store";

import ScoreCircle from "./score-circle.vue";
import XIcon from "../../icons/x-icon.vue";
import BpWarning from "./bp-warning-disclosure.vue"

const props = defineProps({
    warnings: {
        type: Array,
        default: [],
    },
    score: {
        type: Object,
        default: {},
    },
});

const popperClass = "max-w-[100px] !text-center text-white !text-[12px] !leading-[16px] font-[Roboto] tracking-[0.5%]";
const TOOLTIPS_CONTENT = {
    bullets: "50% of your overall score",
    format: "10% of your overall score",
    relevantInfo: "25% of your overall score",
    skills: "15% of your overall score"
};

const hoveredId = ref(null)
const scorerStore = useResumeScorerStore()
const { getScore: score, getWarnings: warnings, showResumeScorer } = storeToRefs(scorerStore)

const head = document.getElementsByTagName('head')[0];
const cssSheet = document.createElement('style');
head.appendChild(cssSheet);
cssSheet.type = "text/css";

onBeforeUnmount(() => {
    cssSheet.remove();
})

const warningHoverHandler = (id) => {
    let css = ''

    if (id === 'highlight-bp-metrics') {
        css = `.highlight-bp {
            background-color: #FFF0D0;
        }`
    } else {
        const bp = document.getElementById(id)

        if (bp != null) {
            if (id === 'pages-nr-exceeded') {
                css = `#${id} {
                    outline: 4px solid #FFD064;
                }`
            } else {
                if(id.startsWith('highlight-')){
                    css = `#${id}>p:nth-of-type(1) {
                        background-color: #FFF0D0 !important;
                    }`
                }else{
                    css = `#${id}{
                        background-color: #FFF0D0 !important;
                    }`
                }
            }
        }
        
        hoveredId.value = id
    }

    if (cssSheet.styleSheet){
        cssSheet.styleSheet.cssText = css;
    } else {
        cssSheet.appendChild(document.createTextNode(css));
    }
}

const warningHoverResetHandler = () => {
    hoveredId.value = null;
    cssSheet.innerText = '';
}

const bulletsProgressWidth = computed(() => {
    const percents = (score?.value?.bulletPointsScore * 100) / 50
    return `${percents}%`;  //50
});

const formattingScore = computed(() => {
    const percents = (score.value?.formattingScore * 100) / 10
    return `${percents}%`;  //10
});

const relevantInfoScore = computed(() => {
    const percents = (score.value?.relevantInformationScore * 100) / 25
    return `${percents}%`;  //20
});

const skillsScore = computed(() => {
    const percents = (score.value?.skillsScore * 100) / 15
    return `${percents}%`;  //15
});
</script>

<style>
.bullets-progress {
    width: v-bind("bulletsProgressWidth");
}

.format-progress {
    width: v-bind("formattingScore");
}

.info-progress {
    width: v-bind("relevantInfoScore");
}

.skills-progress {
    width: v-bind("skillsScore");
}
</style>
